import { EditorContent as ReadOnlyEditorContent } from "@tiptap/react";
import { useRouter } from "next/router";
import { type Dispatch, type SetStateAction, useEffect } from "react";

import ActivityChoiceIndicator from "@/components/ActivityChoiceIndicator";
import ALPHABET from "@/view/constants/alphabet";
import { type Letter } from "@/view/QuizQuestion/constants";
import useMultipleChoiceEditor from "@/view/QuizQuestion/useMultipleChoiceEditor/";
import ReadAloudButton, {
  ButtonTypes,
} from "@/view/ReadAloudButton/ReadAloudButton";
import useReadAloudMode from "@/view/ReadAloudControls/useReadAloudMode";
import { ReadAloudContentModes } from "@/view/ReadAloudControls/useReadAloudMode/constants";
import { listenModeHighlightDecoratorPluginKey } from "@/view/TiptapArticleBody/extensions/ListenModeHighlightDecorator";
import { type INode } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";
import {
  choiceLetterCss,
  choiceTextCss,
  containerCss,
  multipleChoiceBackgroundCss,
  multipleChoiceContainerCss,
  multipleChoiceCss,
  multipleChoiceLabelCss,
} from "./MultipleChoice.style";

interface MultipleChoiceProps {
  choiceIndex: number;
  choiceText: INode | null;
  currentReadAloudChoice: Letter | null;
  isCorrectChoice: boolean | null;
  isReadAloudButtonActive?: boolean;
  isStudentsChoice: boolean | null;
  setCurrentReadAloudChoice: Dispatch<SetStateAction<Letter | null>>;
  shouldHideQuizAnswers?: boolean;
}

export const MultipleChoice = ({
  choiceIndex,
  choiceText,
  currentReadAloudChoice,
  isCorrectChoice,
  isReadAloudButtonActive = false,
  isStudentsChoice,
  setCurrentReadAloudChoice,
  shouldHideQuizAnswers = false,
}: MultipleChoiceProps) => {
  const router = useRouter();
  const {
    query: { levelId },
  } = router;

  const {
    currentSpeech,
    isReadAloudModeActive,
    readAloudContentMode,
    currentMark,
  } = useReadAloudMode();

  const READ_ALOUD_CONTENT_MODE = ReadAloudContentModes.ANSWER;

  const editor = useMultipleChoiceEditor({
    content: choiceText,
    dataTestId: "TipTapQuizQuestionChoice-Editor",
    levelId,
  });

  // Dispatch the transaction and update the editor state (apply highlighting to selected words)
  useEffect(() => {
    if (editor) {
      const metaData =
        !isReadAloudModeActive ||
        readAloudContentMode !== READ_ALOUD_CONTENT_MODE ||
        currentReadAloudChoice !== ALPHABET.toUpperCase()[choiceIndex]
          ? {}
          : { ...currentMark };
      const transaction = editor.state.tr.setMeta(
        listenModeHighlightDecoratorPluginKey,
        metaData,
      );
      editor.view.dispatch(transaction);
    }
  }, [
    JSON.stringify(currentMark),
    isReadAloudModeActive,
    JSON.stringify(choiceText),
  ]);

  // Pause previous quiz answer if we're on a new one
  useEffect(() => {
    if (readAloudContentMode === READ_ALOUD_CONTENT_MODE && currentSpeech) {
      currentSpeech.pauseAudio();
    }
  }, [choiceIndex]);

  if (!editor || !choiceText) return null;

  const revealCorrectAnswer = isCorrectChoice && !shouldHideQuizAnswers;
  const shouldHideLabel = !isStudentsChoice && !revealCorrectAnswer;

  const renderLabel = () => {
    if (shouldHideLabel) return null;

    return (
      <ActivityChoiceIndicator
        __cssFor={multipleChoiceLabelCss}
        labelColor={revealCorrectAnswer ? "success" : "primary"}
        status={revealCorrectAnswer ? "correct" : "primary"}
        text={
          isCorrectChoice && !isStudentsChoice
            ? "Correct answer"
            : "Your answer"
        }
      />
    );
  };

  const alphabeticAnswer = (ALPHABET.toUpperCase()[choiceIndex] ??
    "") as Letter;

  return (
    <li
      css={containerCss}
      data-testid={`quizQuestionuseMultipleChoiceEditorWrapper-${alphabeticAnswer}`}
    >
      <div
        css={[
          multipleChoiceContainerCss(isReadAloudButtonActive),
          multipleChoiceBackgroundCss({
            isCorrectChoice,
            shouldRenderDefaultBackground:
              shouldHideQuizAnswers || shouldHideLabel,
          }),
        ]}
      >
        {renderLabel()}
        <div css={multipleChoiceCss}>
          <span css={choiceLetterCss}>{alphabeticAnswer}</span>
          <span css={choiceTextCss}>
            <ReadOnlyEditorContent
              editor={editor}
              data-testid={`readOnlyEditorMultipleChoiceContent-${alphabeticAnswer}`}
            />
          </span>
        </div>
      </div>
      {isReadAloudButtonActive && (
        <ReadAloudButton
          buttonContentMode={READ_ALOUD_CONTENT_MODE}
          position={alphabeticAnswer}
          setCurrentReadAloudChoice={() =>
            setCurrentReadAloudChoice(alphabeticAnswer)
          }
          text={choiceText}
          type={ButtonTypes.answer}
        />
      )}
    </li>
  );
};

export default MultipleChoice;
